/**
 * Fashion product card quick-add.
 * Compact Material size menu + add. Shows in-cart / wishlist state.
 */
(() => {
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let cartVariantIds = new Set();
  let cartProductIds = new Set();

  const closeAllMenus = (except) => {
    document.querySelectorAll('[data-beisat-size-menu].is-open').forEach((menu) => {
      if (except && menu === except) return;
      menu.classList.remove('is-open');
      const trigger = menu.querySelector('[data-beisat-size-trigger]');
      const list = menu.querySelector('[data-beisat-size-list]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (list) list.hidden = true;
    });
  };

  // The size list ships empty; build it from the hidden select the first time
  // the menu opens (keeps ~2 DOM nodes per size off every card on the page).
  const buildSizeList = (menu) => {
    const list = menu && menu.querySelector('[data-beisat-size-list]');
    const select = menu && menu.querySelector('[data-beisat-size-select]');
    if (!list || !select || list.children.length) return;
    const frag = document.createDocumentFragment();
    Array.from(select.options).forEach((option) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-block__size-option' + (option.selected ? ' is-selected' : '');
      btn.setAttribute('data-beisat-size-option', '');
      btn.setAttribute('data-value', option.value);
      btn.setAttribute('data-size', option.getAttribute('data-size') || option.textContent.trim());
      if (option.dataset.media) btn.setAttribute('data-media', option.dataset.media);
      btn.setAttribute('aria-selected', option.selected ? 'true' : 'false');
      btn.textContent = option.textContent.trim();
      li.appendChild(btn);
      frag.appendChild(li);
    });
    list.appendChild(frag);
  };

  const syncFromSelect = (formRoot) => {
    if (!formRoot) return;
    const select = formRoot.querySelector('[data-beisat-size-select]');
    const input = formRoot.querySelector('[data-beisat-variant-input], input[name="id"]');
    const btn = formRoot.querySelector('[data-beisat-quick-add-btn]');
    const label = formRoot.querySelector('[data-beisat-size-label]');
    if (!input) return;

    if (select) {
      const option = select.options[select.selectedIndex];
      if (!option) return;

      input.value = option.value;
      input.disabled = false;

      const size = option.getAttribute('data-size') || option.textContent.trim();
      formRoot.dataset.selectedSize = size;
      formRoot.dataset.selectedVariantId = option.value;
      if (label) label.textContent = size;
      const trigger = formRoot.querySelector('[data-beisat-size-trigger]');
      if (trigger && size) {
        const sizeName = trigger.getAttribute('data-size-name') || 'Size';
        trigger.setAttribute('aria-label', `${sizeName} ${size}`);
      }
      if (btn) {
        if (size) btn.setAttribute('data-selected-size', size);
        else btn.removeAttribute('data-selected-size');
      }

      formRoot.querySelectorAll('[data-beisat-size-option]').forEach((optBtn) => {
        const selected = optBtn.getAttribute('data-value') === option.value;
        optBtn.classList.toggle('is-selected', selected);
        optBtn.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
    } else {
      formRoot.dataset.selectedVariantId = input.value;
    }

    applyCartState(formRoot);
  };

  const applyCartState = (formRoot) => {
    if (!formRoot) return;
    const variantId = String(formRoot.dataset.selectedVariantId || formRoot.querySelector('[data-beisat-variant-input]')?.value || '');
    const productId = String(formRoot.getAttribute('data-product-id') || '');
    const inCart = (variantId && cartVariantIds.has(variantId)) || (productId && cartProductIds.has(productId));
    formRoot.classList.toggle('is-in-cart', !!inCart);
    const btn = formRoot.querySelector('[data-beisat-quick-add-btn]');
    if (btn) {
      btn.setAttribute('aria-pressed', inCart ? 'true' : 'false');
      if (inCart) btn.setAttribute('data-in-cart', 'true');
      else btn.removeAttribute('data-in-cart');
    }
  };

  const applyCartStateAll = (root) => {
    (root || document).querySelectorAll('product-form[data-beisat-quick-add]').forEach(applyCartState);
  };

  let cartStateRequest = null;

  const refreshCartState = () => {
    if (cartStateRequest) return cartStateRequest;
    cartStateRequest = fetch(`${rootPath}cart.js`, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        cartVariantIds = new Set();
        cartProductIds = new Set();
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach((item) => {
            if (item && item.variant_id != null) cartVariantIds.add(String(item.variant_id));
            if (item && item.product_id != null) cartProductIds.add(String(item.product_id));
          });
        }
        applyCartStateAll(document);
      })
      .catch(() => {})
      .finally(() => {
        cartStateRequest = null;
      });
    return cartStateRequest;
  };

  // Mirror ST wishlist state onto is-active for CSS. Do not MutationObserve —
  // writing class/aria here used to re-enter forever against ST's own observers.
  const syncWishlistButton = (btn) => {
    if (!btn) return;
    const active =
      btn.classList.contains('st-is-added') ||
      btn.classList.contains('active') ||
      btn.classList.contains('st-active') ||
      btn.getAttribute('data-active') === 'true' ||
      btn.getAttribute('aria-pressed') === 'true';
    if (btn.classList.contains('is-active') === active) return;
    btn.classList.toggle('is-active', active);
  };

  const syncWishlistAll = (root) => {
    (root || document).querySelectorAll('.st-wishlist-button, .product-block__wishlist, .beisat-pcard__wishlist').forEach(syncWishlistButton);
  };

  const applySizeChoice = (formRoot, value) => {
    const select = formRoot.querySelector('[data-beisat-size-select]');
    if (!select) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncFromSelect(formRoot);
  };

  const markAdding = (formRoot) => {
    formRoot.classList.add('is-adding');
    window.setTimeout(() => formRoot.classList.remove('is-adding'), 650);
  };

  const submitForm = (formRoot) => {
    const form = formRoot.querySelector('form');
    if (!form) return;

    syncFromSelect(formRoot);
    markAdding(formRoot);

    const size = formRoot.dataset.selectedSize || '';
    if (size) {
      try {
        sessionStorage.setItem('beisat_last_added_size', size);
      } catch (e) {}
    }

    // Optimistic in-cart mark for selected variant
    const variantId = String(formRoot.dataset.selectedVariantId || '');
    const productId = String(formRoot.getAttribute('data-product-id') || '');
    if (variantId) cartVariantIds.add(variantId);
    if (productId) cartProductIds.add(productId);
    applyCartState(formRoot);

    if (typeof form.requestSubmit === 'function') {
      const real = document.createElement('button');
      real.type = 'submit';
      real.name = 'add';
      real.hidden = true;
      form.appendChild(real);
      form.requestSubmit(real);
      real.remove();
      return;
    }
    form.submit();
  };

  const initCards = (root) => {
    (root || document).querySelectorAll('product-form[data-beisat-quick-add]').forEach((formRoot) => {
      syncFromSelect(formRoot);
    });
    syncWishlistAll(root);
    applyCartStateAll(root);
  };

  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest('[data-beisat-size-select]');
      if (!select) return;
      event.stopPropagation();
      const formRoot = select.closest('product-form');
      syncFromSelect(formRoot);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const optionBtn = event.target.closest('[data-beisat-size-option]');
      if (optionBtn) {
        event.preventDefault();
        event.stopPropagation();
        const formRoot = optionBtn.closest('product-form');
        applySizeChoice(formRoot, optionBtn.getAttribute('data-value'));
        closeAllMenus();
        return;
      }

      const trigger = event.target.closest('[data-beisat-size-trigger]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        const menu = trigger.closest('[data-beisat-size-menu]');
        const list = menu && menu.querySelector('[data-beisat-size-list]');
        const willOpen = !menu.classList.contains('is-open');
        closeAllMenus(willOpen ? menu : null);
        if (!menu || !list) return;
        if (willOpen) buildSizeList(menu);
        menu.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        list.hidden = !willOpen;
        return;
      }

      if (event.target.closest('[data-beisat-size-menu]')) {
        event.stopPropagation();
        return;
      }

      closeAllMenus();

      // Cart pill in the price row: submits the card's quick-add form
      // (same size choice / in-cart state as the old + on the image).
      const cartPill = event.target.closest('[data-beisat-pcard-cart]');
      if (cartPill) {
        event.preventDefault();
        event.stopPropagation();
        const card = cartPill.closest('product-block, .beisat-product-card');
        const formRoot = card && card.querySelector('product-form[data-beisat-quick-add]');
        if (formRoot) submitForm(formRoot);
        return;
      }

      const addBtn = event.target.closest('[data-beisat-quick-add-btn]');
      if (addBtn) {
        event.preventDefault();
        event.stopPropagation();
        const formRoot = addBtn.closest('product-form');
        if (formRoot) submitForm(formRoot);
        return;
      }

      const wish = event.target.closest('.st-wishlist-button, .product-block__wishlist');
      if (wish) {
        wish.classList.add('is-tapped');
        window.setTimeout(() => {
          wish.classList.remove('is-tapped');
          syncWishlistButton(wish);
        }, 320);
        window.setTimeout(() => syncWishlistAll(document), 700);
      }
    },
    true
  );

  // Whole card opens the product: taps on the white area (labels, price row,
  // padding) follow the card's product link. Controls keep their own action.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const card = event.target.closest('.beisat-product-card');
    if (!card) return;
    if (event.target.closest('a, button, select, input, textarea, label, summary, form, [role="button"], [data-beisat-size-menu], .st-wishlist-button')) return;
    if (String(window.getSelection && window.getSelection()).trim()) return;
    const link = card.querySelector('a.beisat-pcard__link[href], a.beisat-pcard__media-link[href]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(link.href, '_blank', 'noopener');
    } else {
      window.location.href = link.href;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllMenus();
  });

  ['on:cart:add', 'on:cart:after-merge', 'dispatch:cart-drawer:refresh'].forEach((name) => {
    document.addEventListener(name, () => {
      window.setTimeout(refreshCartState, 120);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCards(document);
      refreshCartState();
    });
  } else {
    initCards(document);
    refreshCartState();
  }
  document.addEventListener('shopify:section:load', (e) => {
    initCards(e.target);
    refreshCartState();
  });
  document.addEventListener('beisat:recent-products:loaded', (e) => {
    initCards(e.detail?.section || document);
    refreshCartState();
  });
  document.addEventListener('beisat:related-products:loaded', (e) => {
    initCards(e.detail?.section || document);
    refreshCartState();
  });
  document.addEventListener('beisat:cart-cross-sell:loaded', (e) => {
    initCards(e.detail?.root || document);
    refreshCartState();
  });
})();
